import { useEffect, useRef, useState } from "react";
import { ErrorCodes, ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import type {
  CustomerSearchBy,
  PartyDetail,
  PartySummary,
  UpdatePartyInput,
} from "../../api/party-contracts";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Select,
  Table,
  TableCard,
  Toast,
  ToastStack,
} from "../../ui";
import {
  useCustomerDetail,
  useCustomerSearch,
  useCustomers,
  useResetPassword,
  useUpdateCustomer,
} from "./use-customers";

// Müşteriler — the admin customer-management surface (PII-aware, governance tone).
// List/search parties (masked-free summary), open a detail (masked TCKN, hasAccount),
// edit name + email (party:write), and — for a linked account — trigger a step-up'd
// one-time password reset (customer.account:reset). RBAC is UX: the write affordances
// are grant-gated, the API re-enforces every call regardless. No raw sensitive data is
// shown beyond what the API returns; the temp-password reveal is the one careful
// exception (shown once, copy-once, never persisted).
export function Customers() {
  const { t } = useT();
  const { me } = usePrincipal();
  const canWrite = hasGrant(me, "party:write", "own");
  const canReset = hasGrant(me, "customer.account:reset", "all");

  const [by, setBy] = useState<CustomerSearchBy>("name");
  const [draft, setDraft] = useState("");
  const [term, setTerm] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  const list = useCustomers(term.trim() === "");
  const search = useCustomerSearch(by, term);
  const searching = term.trim() !== "";

  // D12 — the list is keyset-paginated; flatten the fetched pages.
  const listRows: PartySummary[] | undefined = list.data
    ? list.data.pages.flatMap((p) => p.data ?? [])
    : undefined;
  const rows: PartySummary[] | undefined = searching ? search.data?.parties : listRows;
  const isLoading = searching ? search.isLoading : list.isLoading;
  const error = searching ? search.error : list.error;

  const selectedRow = rows?.find((r) => r.id === selected) ?? null;

  function submitSearch() {
    setSelected(null);
    setTerm(draft);
  }

  function clearSearch() {
    setDraft("");
    setTerm("");
    setSelected(null);
  }

  return (
    <section className="surf-stack">
      <div className="surf-head">
        <div>
          <h1 className="surf-title">{t("cust.heading")}</h1>
          <p className="surf-sub muted">{t("cust.sub")}</p>
        </div>
      </div>

      <TableCard title={t("cust.list.title")} data-testid="cust-list">
        <form
          className="cust-search"
          data-testid="cust-search-form"
          onSubmit={(e) => {
            e.preventDefault();
            submitSearch();
          }}
        >
          <fieldset className="k-form-grid">
            <Select
              label={t("cust.search.by")}
              value={by}
              onChange={(e) => setBy(e.target.value as CustomerSearchBy)}
            >
              <option value="name">{t("cust.search.byName")}</option>
              <option value="phone">{t("cust.search.byPhone")}</option>
            </Select>
            <Input
              label={t("cust.search.term")}
              value={draft}
              placeholder={by === "phone" ? "+90…" : t("cust.search.namePlaceholder")}
              onChange={(e) => setDraft(e.target.value)}
              data-testid="cust-search-input"
            />
          </fieldset>
          <div className="k-form-actions">
            <Button variant="primary" type="submit" disabled={draft.trim() === ""}>
              {t("cust.search.submit")}
            </Button>
            {searching && (
              <Button variant="ghost" type="button" onClick={clearSearch}>
                {t("cust.search.clear")}
              </Button>
            )}
          </div>
        </form>

        {isLoading && <LoadingState label={t("cust.loading")} rows={3} />}
        {error instanceof ProblemError && (
          <ErrorState title={t("cust.error")} description={error.problem.detail} />
        )}
        {rows && rows.length === 0 && (
          <EmptyState
            data-testid="cust-empty"
            icon="member"
            title={searching ? t("cust.empty.search") : t("cust.empty.list")}
          />
        )}
        {rows && rows.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>{t("cust.col.name")}</th>
                <th>{t("cust.col.phone")}</th>
                <th>{t("cust.col.email")}</th>
                <th className="cust-td-right" />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} data-testid="cust-row">
                  <td className="cust-strong">
                    {p.firstName} {p.lastName}
                  </td>
                  <td>{p.phone ?? "—"}</td>
                  <td>{p.email ?? "—"}</td>
                  <td className="cust-td-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      data-testid="cust-open-btn"
                      onClick={() => setSelected((s) => (s === p.id ? null : p.id))}
                    >
                      {t("cust.open")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {/* D12 — the "daha fazla" lane. Without it every customer past the first
            page was unreachable: the Page envelope's nextCursor was discarded and
            name search is own-book scoped, so it is not a substitute. */}
        {!searching && list.hasNextPage && (
          <div className="k-form-actions">
            <Button
              variant="secondary"
              type="button"
              data-testid="cust-more"
              loading={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
            >
              {t("cust.more")}
            </Button>
          </div>
        )}
      </TableCard>

      {selected && (
        <CustomerDetail
          id={selected}
          fallbackHouseholdId={selectedRow?.householdId ?? null}
          canWrite={canWrite}
          canReset={canReset}
        />
      )}
    </section>
  );
}

// The customer detail card: masked-only PII (grounded on what the API returns),
// an inline edit mode for name + email, and the step-up'd password reset.
function CustomerDetail({
  id,
  fallbackHouseholdId,
  canWrite,
  canReset,
}: {
  id: string;
  fallbackHouseholdId: string | null;
  canWrite: boolean;
  canReset: boolean;
}) {
  const { t } = useT();
  const detail = useCustomerDetail(id);

  return (
    <div className="surf-stack" data-testid="cust-detail">
      {detail.isLoading && (
        <Card>
          <LoadingState label={t("cust.detail.loading")} rows={3} />
        </Card>
      )}
      {detail.error instanceof ProblemError && (
        <ErrorState title={t("cust.detail.error")} description={detail.error.problem.detail} />
      )}
      {detail.data && (
        <CustomerDetailCard
          id={id}
          party={detail.data}
          fallbackHouseholdId={fallbackHouseholdId}
          canWrite={canWrite}
          canReset={canReset}
        />
      )}
    </div>
  );
}

function CustomerDetailCard({
  id,
  party,
  fallbackHouseholdId,
  canWrite,
  canReset,
}: {
  id: string;
  party: PartyDetail;
  fallbackHouseholdId: string | null;
  canWrite: boolean;
  canReset: boolean;
}) {
  const { t } = useT();
  const update = useUpdateCustomer(id);

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState(party.firstName);
  const [lastName, setLastName] = useState(party.lastName);
  const [email, setEmail] = useState(party.email ?? "");
  const [savedToast, setSavedToast] = useState(false);

  const maskedTckn = party.sensitive.tckn ?? "—";
  const householdId = party.householdId ?? fallbackHouseholdId;

  function beginEdit() {
    setFirstName(party.firstName);
    setLastName(party.lastName);
    setEmail(party.email ?? "");
    update.reset();
    setEditing(true);
  }

  // The change-set: only fields that actually differ (email is never sent empty —
  // the schema rejects a blank, and clearing an email is out of scope here).
  const patch: UpdatePartyInput = {};
  if (firstName.trim() !== party.firstName) patch.firstName = firstName.trim();
  if (lastName.trim() !== party.lastName) patch.lastName = lastName.trim();
  if (email.trim() !== (party.email ?? "") && email.trim() !== "") patch.email = email.trim();

  const dirty = Object.keys(patch).length > 0;
  const namesFilled = firstName.trim() !== "" && lastName.trim() !== "";
  const canSave = canWrite && dirty && namesFilled && !update.isPending;

  function save() {
    update.mutate(patch, {
      onSuccess: () => {
        setEditing(false);
        setSavedToast(true);
      },
    });
  }

  return (
    <>
      <Card className="cust-detailcard" data-testid="cust-detailcard">
        <div className="surf-cardhead">
          <span className="surf-cardtitle">
            <Icon name="member" size={16} strokeWidth={1.9} /> {party.firstName} {party.lastName}
          </span>
          <span className="cust-account">
            {party.hasAccount ? (
              <Badge variant="success">{t("cust.account.yes")}</Badge>
            ) : (
              <Badge variant="standard">{t("cust.account.no")}</Badge>
            )}
          </span>
        </div>

        <form
          data-testid="cust-edit-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) save();
          }}
        >
          <fieldset className="k-form-grid" disabled={!editing}>
            <Input
              label={t("cust.field.firstName")}
              value={editing ? firstName : party.firstName}
              readOnly={!editing}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Input
              label={t("cust.field.lastName")}
              value={editing ? lastName : party.lastName}
              readOnly={!editing}
              onChange={(e) => setLastName(e.target.value)}
            />
            <Input
              label={t("cust.field.email")}
              type="email"
              value={editing ? email : (party.email ?? "")}
              readOnly={!editing}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label={t("cust.field.phone")}
              value={party.phone ?? "—"}
              readOnly
              hint={t("cust.field.phoneNote")}
            />
            <Input
              label={t("cust.field.tckn")}
              value={maskedTckn}
              readOnly
              hint={t("cust.field.tcknNote")}
            />
            <Input label={t("cust.field.householdId")} value={householdId ?? "—"} readOnly />
          </fieldset>

          <div className="k-form-actions">
            {!editing && canWrite && (
              <Button
                variant="secondary"
                type="button"
                data-testid="cust-edit-btn"
                onClick={beginEdit}
              >
                {t("cust.edit")}
              </Button>
            )}
            {editing && (
              <>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!canSave}
                  loading={update.isPending}
                >
                  {t("cust.save")}
                </Button>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => setEditing(false)}
                  disabled={update.isPending}
                >
                  {t("cust.cancel")}
                </Button>
              </>
            )}
            {!canWrite && <span className="muted surf-note">{t("cust.readOnly")}</span>}
            {update.error instanceof ProblemError && (
              <span role="alert" className="form-error" data-testid="cust-edit-error">
                {update.error.problem.detail}
              </span>
            )}
          </div>
        </form>

        {party.hasAccount && canReset && <ResetPassword id={id} />}
      </Card>

      {savedToast && (
        <ToastStack>
          <Toast
            tone="success"
            title={t("cust.save.success")}
            closeLabel={t("cust.toast.dismiss")}
            onClose={() => setSavedToast(false)}
          />
        </ToastStack>
      )}
    </>
  );
}

// Password reset — a step-up'd, one-time temporary password. The ApiClient handles
// the 403/CSA-1007 → interactive MFA → retry transparently; if the admin abandons the
// step-up (or Keycloak is unconfigured → 503), the ProblemJson is surfaced cleanly.
// On success the temp value is revealed ONCE, with a copy affordance and a clear
// "convey out-of-band, won't be shown again" note — and is never cached.
function ResetPassword({ id }: { id: string }) {
  const { t } = useT();
  const reset = useResetPassword(id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  function confirmReset() {
    reset.mutate(undefined, {
      onSuccess: (d) => {
        setConfirmOpen(false);
        setTempPassword(d.temporaryPassword);
      },
      onError: () => setConfirmOpen(false),
    });
  }

  function closeReveal() {
    setTempPassword(null);
    reset.reset();
  }

  const isStepUp =
    reset.error instanceof ProblemError && reset.error.is(ErrorCodes.STEP_UP_REQUIRED);

  return (
    <div className="cust-reset" data-testid="cust-reset">
      <div className="cust-reset__head">
        <span className="surf-cardtitle">{t("cust.reset.title")}</span>
      </div>
      <p className="muted surf-note">{t("cust.reset.note")}</p>

      <div className="k-form-actions">
        <Button
          variant="danger"
          type="button"
          data-testid="cust-reset-btn"
          onClick={() => setConfirmOpen(true)}
        >
          {t("cust.reset.action")}
        </Button>
        {isStepUp && (
          <span role="alert" className="form-error" data-testid="cust-reset-stepup">
            {t("cust.reset.stepUp")}
          </span>
        )}
        {reset.error instanceof ProblemError && !isStepUp && (
          <span role="alert" className="form-error" data-testid="cust-reset-error">
            {reset.error.problem.detail}
          </span>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        tone="danger"
        icon="lock"
        title={t("cust.reset.confirmTitle")}
        description={t("cust.reset.confirmBody")}
        cancelLabel={t("cust.cancel")}
        confirmLabel={t("cust.reset.action")}
        confirming={reset.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmReset}
      />

      {tempPassword && <TempPasswordDialog password={tempPassword} onClose={closeReveal} />}
    </div>
  );
}

// The one careful sensitive reveal. Reuses the kit's overlay/dialog chrome; shows the
// temporary password once with a copy affordance and an unmistakable "convey once,
// won't be shown again" caption.
function TempPasswordDialog({ password, onClose }: { password: string; onClose: () => void }) {
  const { t } = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function copy() {
    try {
      await navigator.clipboard?.writeText(password);
      setCopied(true);
    } catch {
      // Clipboard unavailable (permissions / non-secure context) — the value is
      // still visible for manual transcription.
    }
  }

  return (
    <div className="k-overlay" onClick={onClose}>
      <div
        className="k-dialog"
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={t("cust.reset.revealTitle")}
        data-testid="cust-reset-reveal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="k-dialog__icon k-dialog__icon--primary">
          <Icon name="vault" size={23} strokeWidth={1.9} />
        </div>
        <div className="k-dialog__title">{t("cust.reset.revealTitle")}</div>
        <div className="k-dialog__desc">{t("cust.reset.revealBody")}</div>

        <div className="cust-temp">
          <code className="cust-temp__value" data-testid="cust-temp-value">
            {password}
          </code>
          <Button variant="secondary" size="sm" type="button" onClick={() => void copy()}>
            {copied ? t("cust.reset.copied") : t("cust.reset.copy")}
          </Button>
        </div>

        <div className="k-dialog__actions">
          <Button variant="primary" type="button" onClick={onClose}>
            {t("cust.reset.done")}
          </Button>
        </div>
      </div>
    </div>
  );
}
